import { type TableProps, Tag } from 'antd';
import React, { type JSX } from 'react';

import { DeactivateAction } from '@client/components/actions/deactivate.action';
import { EditAction } from '@client/components/actions/edit.action';
import { StatusTag } from '@client/components/StatusTag';
import { EAbilitySubjects } from '@client/hooks/useAbilities';
import { getTranslatedMessage } from '@client/utils/i18n.util';
import { actionField } from '@client/utils/table/action.util';
import { columnsCache } from '@client/utils/table/column.util';
import {
  columnFilter,
  type TColumnFilter,
} from '@client/utils/table/filter.util';
import { columnSorter } from '@client/utils/table/sorter.util';
import { indexColumn } from '@client/utils/table/table.util';
import { tsToLocaleDateTime } from '@common/utils';

import type { TFilters, TSorts } from '@client/hooks/useTable';
import type { ITenant, IMetadata, EStatus, ELocales } from '@common/types';

type TArgs = {
  locale: ELocales;
  sortedInfo: TSorts;
  filteredInfo: TFilters;
  entities: ITenant[];
  onDelete?: (entity: ITenant) => void;
  onEdit?: (id: string) => void;
};

export const columnsMetadata = (
  props: TArgs,
  filterIn: string[] = [],
): TableProps<ITenant>['columns'] => {
  const { sortedInfo, entities, filteredInfo, locale, onEdit, onDelete } =
    props;

  const { cached, updateCached } = columnsCache<ITenant>({
    sortedInfo,
    filteredInfo,
    entities,
    locale,
    filterIn,
    onEdit,
    onDelete,
  });

  if (cached) {
    return cached;
  }

  const t = getTranslatedMessage;

  const actionsColumn =
    onEdit || onDelete
      ? {
          ...actionField(),
          render: (record: ITenant): React.JSX.Element => (
            <div>
              <EditAction
                subject={EAbilitySubjects.TENANT}
                onEdit={onEdit}
                type={'text'}
                entityId={record.id}
              />
              <DeactivateAction<ITenant>
                subject={EAbilitySubjects.TENANT}
                onDelete={onDelete}
                type={'text'}
                entity={record}
                translateKey={t('tenant')}
              />
            </div>
          ),
        }
      : null;

  const columns = [
    indexColumn,
    {
      title: t('tenant.name'),
      dataIndex: 'name',
      key: 'name',
      filterSearch: true,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'name',
      ) as TColumnFilter<ITenant>),
      ...columnSorter(sortedInfo, 'name', 'name', 1),
    },
    {
      title: t('form.status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      align: 'center',
      concealable: true,
      render: (status: keyof typeof EStatus): JSX.Element => (
        <StatusTag status={status} />
      ),
    },
    {
      title: t('form.type'),
      dataIndex: 'type',
      key: 'type',
      width: 150,
      align: 'center',
      concealable: true,
      render: (type: string): JSX.Element => <Tag>{type}</Tag>,
    },
    {
      title: t('form.createdAt'),
      width: 200,
      dataIndex: 'metadata',
      align: 'center',
      key: 'metadata.createdAt',
      hidden: true,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'metadata.createdAt',
        (createdAt: IMetadata['createdAt']) => tsToLocaleDateTime(createdAt),
      ) as TColumnFilter<ITenant>),
      ...columnSorter(sortedInfo, 'metadata', 'metadata.createdAt', 2),
      render: ({ createdAt }: IMetadata): JSX.Element => (
        <div>{createdAt ? tsToLocaleDateTime(createdAt) : null}</div>
      ),
    },
    {
      title: t('form.updatedAt'),
      width: 200,
      dataIndex: 'metadata',
      align: 'center',
      key: 'metadata.updatedAt',
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'metadata.updatedAt',
        (updatedAt: IMetadata['updatedAt']) => tsToLocaleDateTime(updatedAt),
      ) as TColumnFilter<ITenant>),
      ...columnSorter(sortedInfo, 'metadata', 'metadata.updatedAt', 3),
      render: ({ updatedAt }: IMetadata): JSX.Element => (
        <div>{updatedAt ? tsToLocaleDateTime(updatedAt) : null}</div>
      ),
    },
  ];

  if (actionsColumn) columns.push(actionsColumn);

  const result = filterIn.length
    ? columns.filter(
        (column) =>
          filterIn.includes(column?.dataIndex as string) ||
          column.dataIndex === 'idx',
      )
    : columns;

  return updateCached(result);
};
